// The redesigned builder's three sections (SPEC §1) and how they map onto a
// native definition. Pure: no React, no I/O.
//
//   Include people who match [all|any] of these rules  → the include rules
//   Always include these people                         → contactIds (hand-picked)
//   Exclude anyone who matches                          → not(any[exclusions])
//
// Composed shape: `all[...include, not(any[exclusions])]` for "all", and
// `all[any[...include], not(any[exclusions])]` for "any". The backend's exact
// preview reads exclusions as the `not` children of a top-level `all`, so this
// shape gives the breakdown its "Excluded" number. A saved definition that
// doesn't decompose this way is "non-canonical" and opens read-only.
import { DATE_UNIT_SECONDS } from '@charitystack/schemas';
import type { AudienceCondition, AudienceRule, NativeAudienceCatalog, NativeAudienceDefinition } from '@charitystack/schemas';
import { orgCurrency as sharedOrgCurrency } from '@charitystack/shared/currency';
import { valueWords } from './valueOptions';

type FieldOptions = NativeAudienceCatalog['fieldOptions'];

type GivingCondition = Extract<AudienceCondition, { type: 'giving' }>;

export type MatchMode = 'all' | 'any';
/** One include row: a condition, or a one-level group of conditions. */
export type IncludeRule = AudienceCondition | { type: 'all' | 'any'; children: AudienceCondition[] };

export type SegmentSections = {
  match: MatchMode;
  include: IncludeRule[];
  people: string[];
  exclusions: AudienceCondition[];
};

const isGroup = (rule: AudienceRule): rule is { type: 'all' | 'any'; children: AudienceRule[] } => rule.type === 'all' || rule.type === 'any';
const isCondition = (rule: AudienceRule): rule is AudienceCondition => !isGroup(rule) && rule.type !== 'not';

function asIncludeRule(rule: AudienceRule): IncludeRule | null {
  if (isCondition(rule)) return rule;
  if (isGroup(rule) && rule.children.every(isCondition)) return { type: rule.type, children: rule.children as AudienceCondition[] };
  return null;
}

function asExclusions(rule: AudienceRule): AudienceCondition[] | null {
  if (rule.type !== 'not') return null;
  if (isCondition(rule.child)) return [rule.child];
  if (rule.child.type === 'any' && rule.child.children.length > 0 && rule.child.children.every(isCondition)) return rule.child.children as AudienceCondition[];
  return null;
}

/** The definition as builder sections, or null when it doesn't fit them (open read-only). */
export function toSections(definition: NativeAudienceDefinition): SegmentSections | null {
  if (definition.kind === 'fixed') return { match: 'all', include: [], people: [...definition.contactIds], exclusions: [] };
  const people = definition.kind === 'hybrid' ? [...definition.contactIds] : [];
  const rule = definition.rule;
  if (isCondition(rule)) return { match: 'all', include: [rule], people, exclusions: [] };
  if (rule.type === 'not') return null;
  const exclusions: AudienceCondition[] = [];
  const rest: AudienceRule[] = [];
  if (rule.type === 'all') {
    for (const child of rule.children) {
      if (child.type === 'not') {
        const excluded = asExclusions(child);
        if (!excluded) return null;
        exclusions.push(...excluded);
      } else rest.push(child);
    }
  } else rest.push(...rule.children);
  // "any" mode: either the whole rule is an `any`, or an `all` whose only non-exclusion child is an `any` group
  // (what fromSections writes for "any", with or without exclusions). The two shapes select the same people.
  if (rule.type === 'any' || (rest.length === 1 && rest[0]!.type === 'any')) {
    const children = rule.type === 'any' ? rest : (rest[0] as { children: AudienceRule[] }).children;
    const include = children.map(asIncludeRule);
    if (include.some((item) => item === null) || rule.type === 'any' && exclusions.length) return null;
    return { match: 'any', include: include as IncludeRule[], people, exclusions };
  }
  const include = rest.map(asIncludeRule);
  if (include.some((item) => item === null)) return null;
  return { match: 'all', include: include as IncludeRule[], people, exclusions };
}

/** A typed text or tag value as it is matched: without the spaces around it (the box keeps what was typed). */
function trimmedCondition(condition: AudienceCondition): AudienceCondition {
  if (condition.type === 'text') return { ...condition, values: condition.values.map((value) => value.trim()) };
  if (condition.type === 'list') return { ...condition, value: condition.value.trim() };
  return condition;
}

function trimmedRule(rule: IncludeRule): IncludeRule {
  return 'children' in rule ? { ...rule, children: rule.children.map(trimmedCondition) } : trimmedCondition(rule);
}

/** The sections as a native definition, or null when there's nothing to save yet. */
export function fromSections(sections: SegmentSections): NativeAudienceDefinition | null {
  const people = [...new Set(sections.people)];
  const exclusions = sections.exclusions.map(trimmedCondition);
  const exclusion: AudienceRule[] = exclusions.length
    ? [{ type: 'not', child: exclusions.length === 1 ? exclusions[0]! : { type: 'any', children: exclusions } }]
    : [];
  if (sections.include.length === 0) {
    // Hand-picked only. Exclusions need rules to apply to, so they can't be saved alone.
    return people.length && !exclusion.length ? { version: '1', kind: 'fixed', contactIds: people } : null;
  }
  const included = sections.include.map(trimmedRule);
  const include: AudienceRule[] = sections.match === 'all' || included.length === 1
    ? included
    : [{ type: 'any', children: included }];
  const rule: AudienceRule = { type: 'all', children: [...include, ...exclusion] };
  return people.length ? { version: '1', kind: 'hybrid', rule, contactIds: people } : { version: '1', kind: 'dynamic', rule };
}

/**
 * The sections as the definition to keep in the stored draft. When there's
 * nothing saveable yet (every include rule removed, or exclusions alone) it is
 * still the definition `toSections` reads back as exactly these sections, so a
 * reload shows what was on screen instead of rules the person had removed.
 * Save stays blocked by the builder until `fromSections` returns a definition.
 */
export function toDraftDefinition(sections: SegmentSections): NativeAudienceDefinition {
  const saveable = fromSections(sections);
  if (saveable) return saveable;
  const people = [...new Set(sections.people)];
  if (!sections.exclusions.length) return { version: '1', kind: 'fixed', contactIds: people };
  const rule: AudienceRule = { type: 'all', children: [{ type: 'not', child: sections.exclusions.length === 1 ? sections.exclusions[0]! : { type: 'any', children: sections.exclusions } }] };
  return people.length ? { version: '1', kind: 'hybrid', rule, contactIds: people } : { version: '1', kind: 'dynamic', rule };
}

export type SegmentKindLabel = 'Updates automatically' | 'Fixed list';

/** The kind's one name on every segment page: the list, the builder and the detail. */
export function membershipLabel(membership: 'static' | 'dynamic'): SegmentKindLabel {
  return membership === 'static' ? 'Fixed list' : 'Updates automatically';
}

/** "Updates automatically" when rules decide who's in; "Fixed list" when only hand-picked people do. */
export function kindLabel(definition: NativeAudienceDefinition): SegmentKindLabel {
  return membershipLabel(definition.kind === 'fixed' ? 'static' : 'dynamic');
}

// ── Field presentation ───────────────────────────────────────────────────────

export type FieldGroup = 'Payments' | 'Subscriptions' | 'Contact';

/**
 * Builder labels, groups and menu hints in the dashboard's own words (Paper 05;
 * segment rule fields plan §4.1). A `money` hint shows the org's currency
 * symbol. Unknown fields fall back to the catalog label under Contact.
 */
export const FIELD_PRESENTATION: Record<string, { label: string; group: FieldGroup; hint: string }> = {
  money_donated: { label: 'Total amount', group: 'Payments', hint: 'money' },
  number_donations: { label: 'Payment count', group: 'Payments', hint: 'count' },
  average_gift: { label: 'Average payment', group: 'Payments', hint: 'money' },
  first_donation_at: { label: 'First payment', group: 'Payments', hint: 'date' },
  last_donation_at: { label: 'Last payment', group: 'Payments', hint: 'date' },
  is_lybunt: { label: 'Paid last year, not this year', group: 'Payments', hint: 'yes / no' },
  last_charge_failed: { label: 'Failed payment', group: 'Payments', hint: 'date' },
  // Fund and form rules (fund-form rules plan §4.4) are all time and USD only, whatever the org's currency.
  'giving-fund': { label: 'Gave to fund', group: 'Payments', hint: 'list' },
  'giving-form': { label: 'Gave through form', group: 'Payments', hint: 'list' },
  has_active_subscription: { label: 'Active subscription', group: 'Subscriptions', hint: 'yes / no' },
  subscription_amount: { label: 'Subscription amount', group: 'Subscriptions', hint: 'money' },
  subscription_frequency: { label: 'Subscription frequency', group: 'Subscriptions', hint: 'list' },
  subscription_fund: { label: 'Subscription fund', group: 'Subscriptions', hint: 'text' },
  donor_status: { label: 'Lifecycle stage', group: 'Contact', hint: 'list' },
  signup_source: { label: 'Source', group: 'Contact', hint: 'list' },
  tags: { label: 'Tags', group: 'Contact', hint: 'list' },
  country: { label: 'Country', group: 'Contact', hint: 'text' },
  state: { label: 'State', group: 'Contact', hint: 'text' },
  city: { label: 'City', group: 'Contact', hint: 'text' },
  full_name: { label: 'Name', group: 'Contact', hint: 'text' },
};

export const FIELD_GROUP_ORDER: FieldGroup[] = ['Payments', 'Subscriptions', 'Contact'];

export const MONEY_FIELDS = new Set(['money_donated', 'average_gift', 'subscription_amount']);

/** A form as the dashboard holds it (`selectableForms`). */
export type SegmentForm = { formID?: unknown; formName?: unknown };

/** "Form …a1b2" when the form isn't in the dashboard's list (or no list is at hand, as in the assistant card). */
export function formFallbackName(formId: string): string {
  return `Form …${formId.slice(-4)}`;
}

// A giving option's label ends with its payment count ("General Fund · 1,204 payments"). A
// marketing backend deployed before decision D11 still says "gifts", so both are read.
const GIFT_COUNT_SUFFIX = / · [\d,]+ (?:gifts?|payments?)$/;

/** A giving option's name without its gift count. */
export function givingOptionName(label: string): string {
  return label.replace(GIFT_COUNT_SUFFIX, '');
}

/**
 * The catalog with each form option named by the dashboard's current form name
 * (`selectableForms`), keeping its gift count; the server's name is the fallback.
 */
export function withFormNames<T extends Pick<NativeAudienceCatalog, 'fieldOptions'>>(catalog: T, forms: readonly SegmentForm[]): T {
  const options = catalog.fieldOptions?.['giving-form'];
  if (!options?.length || !forms.length) return catalog;
  const names = new Map(forms.flatMap((form) => (typeof form.formID === 'string' && typeof form.formName === 'string' && form.formName.trim() ? [[form.formID, form.formName.trim()] as const] : [])));
  return {
    ...catalog,
    fieldOptions: {
      ...catalog.fieldOptions,
      'giving-form': options.map((option) => {
        const name = names.get(option.values[0] ?? '');
        return name ? { ...option, label: `${name}${option.label.match(GIFT_COUNT_SUFFIX)?.[0] ?? ''}` } : option;
      }),
    },
  };
}

/** The funds' names, or the forms' names, of a giving rule, in the dashboard's words. */
export function givingScopeNames(condition: GivingCondition, fieldOptions: FieldOptions = {}): string[] {
  if (condition.scope.kind === 'fund') return condition.scope.funds;
  const options = fieldOptions['giving-form'] ?? [];
  return condition.scope.formIds.map((id) => {
    const option = options.find((item) => item.values.includes(id));
    return option ? givingOptionName(option.label) : formFallbackName(id);
  });
}

const GIVING_AMOUNT_WORDS = { gt: 'more than', gte: 'at least', lt: 'less than', lte: 'no more than', eq: 'exactly', neq: 'other than' } as const;
const GIVING_COUNT_WORDS = { gt: 'more than', gte: 'at least', lt: 'fewer than', lte: 'no more than', eq: 'exactly', neq: 'other than' } as const;

/**
 * A giving rule as a phrase: "Gave to General Fund", "Gave at least $100 to any
 * one of General Fund, Zakat", "Made at least 3 payments through Spring Appeal".
 * Several funds or forms match on any one of them; amounts are never added up.
 */
export function describeGiving(condition: GivingCondition, fieldOptions: FieldOptions = {}): string {
  const names = givingScopeNames(condition, fieldOptions);
  const target = `${condition.scope.kind === 'fund' ? 'to' : 'through'} ${names.length > 1 ? 'any one of ' : ''}${names.join(', ')}`;
  if (condition.measure === 'count' && condition.operator === 'gte' && condition.value === 1) return `Gave ${target}`;
  if (condition.measure === 'amount') return `Gave ${GIVING_AMOUNT_WORDS[condition.operator]} ${formatMoney(condition.value / 100, 'USD')} ${target}`;
  return `Made ${GIVING_COUNT_WORDS[condition.operator]} ${condition.value.toLocaleString('en-US')} ${condition.value === 1 ? 'payment' : 'payments'} ${target}`;
}

/** The distinct field ids a saved definition uses, for save analytics (never values). Walks the
 * whole rule tree, so advanced definitions the three-section builder can't show still report. */
export function definitionFieldIds(definition: NativeAudienceDefinition): string[] {
  if (definition.kind === 'fixed') return [];
  const fields = new Set<string>();
  const walk = (rule: AudienceRule): void => {
    if (isGroup(rule)) rule.children.forEach(walk);
    else if (rule.type === 'not') walk(rule.child);
    else fields.add(conditionField(rule));
  };
  walk(definition.rule);
  return [...fields].sort();
}

export function fieldLabel(field: string, catalogLabel?: string): string {
  return FIELD_PRESENTATION[field]?.label ?? catalogLabel ?? field;
}

export function conditionField(condition: AudienceCondition): string {
  return condition.type === 'giving' ? (condition.scope.kind === 'fund' ? 'giving-fund' : 'giving-form') : condition.field;
}

/**
 * The org's currency (decisions.md ADM-136, Q4). Delegates to the shared `orgCurrency` helper
 * (CON-320, `@charitystack/shared/currency`) so this is the single source of truth for the
 * CA/CAD check — never re-implement it here. Call sites in this package still pass `unknown`
 * merchant shapes, so this keeps that wider signature and narrows for the shared helper.
 */
export function orgCurrency(merchant: unknown): 'USD' | 'CAD' {
  return sharedOrgCurrency(merchant as { countryCode?: string | null } | null | undefined);
}

export function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: Number.isInteger(value) ? 0 : 2 }).format(value);
}

// ── Sentences ("Who's in it", template cards) ────────────────────────────────

const NUMBER_WORDS = { gt: 'is more than', gte: 'is at least', lt: 'is less than', lte: 'is no more than', eq: 'is', neq: 'is not' } as const;
const DATE_UNIT_WORDS = { minute: 'minute', hour: 'hour', day: 'day', week: 'week', month: 'month' } as const;

type AgeComparison = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';
/**
 * "{field} {phrase}" for a relative-date ("age") condition. Every comparison reads
 * differently: the builder only creates lt ("is in the last") and gt ("is more than
 * … ago"), but saved and assistant-proposed rules can carry any of the six.
 * A month is a fixed 30 days when the rule is evaluated, not a calendar month, so a
 * month span also names its day count: "is in the last 6 months (180 days)".
 */
export function agePhrase(comparison: AgeComparison, amount: number, unit: keyof typeof DATE_UNIT_WORDS): string {
  const days = unit === 'month' ? ` (${(amount * DATE_UNIT_SECONDS.month) / DATE_UNIT_SECONDS.day} days)` : '';
  const span = `${amount} ${DATE_UNIT_WORDS[unit]}${amount === 1 ? '' : 's'}${days}`;
  switch (comparison) {
    case 'lt': return `is in the last ${span}`;
    case 'lte': return `is ${span} ago or less`;
    case 'gt': return `is more than ${span} ago`;
    case 'gte': return `is at least ${span} ago`;
    case 'eq': return `is exactly ${span} ago`;
    case 'neq': return `is not exactly ${span} ago`;
  }
}

/**
 * A relative-date comparison as the builder's tokens show it, worded by `agePhrase`:
 * the words before the amount ("is exactly") and after the unit ("ago", or "" for "is in the last").
 */
export function ageComparisonWords(comparison: AgeComparison): { before: string; after: string } {
  const phrase = agePhrase(comparison, 2, 'day');
  const [before = '', after = ''] = phrase.split(' 2 days');
  return { before, after: after.trim() };
}

// A date rule stored as the start of a calendar day, in whatever offset it was written:
// the builder writes the viewer's local midnight, the assistant writes UTC midnight ("…T00:00:00Z").
const START_OF_DAY = /^(\d{4})-(\d{2})-(\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * The calendar day a stored date rule names, as YYYY-MM-DD. A start-of-day value reads as the day
 * it was written for, whichever timezone it was written in, so the box and the sentence agree with
 * what was saved; any other instant reads as the viewer's local day. '' when it isn't a date.
 */
export function calendarDayOf(iso: string): string {
  const day = START_OF_DAY.exec(iso);
  if (day) return `${day[1]}-${day[2]}-${day[3]}`;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDate(iso: string): string {
  const day = calendarDayOf(iso);
  if (!day) return iso;
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const utc = new Date(Date.UTC(2000, 0, 1));
  utc.setUTCFullYear(year, month - 1, date);
  return utc.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** One condition as a phrase: "Total amount is more than $500". */
export function describeCondition(condition: AudienceCondition, currency: string, labelFor: (field: string) => string = fieldLabel, fieldOptions: FieldOptions = {}): string {
  if (condition.type === 'giving') return describeGiving(condition, fieldOptions);
  const label = labelFor(condition.field);
  switch (condition.type) {
    case 'number':
      return `${label} ${NUMBER_WORDS[condition.operator]} ${MONEY_FIELDS.has(condition.field) ? formatMoney(condition.value, currency) : condition.value.toLocaleString('en-US')}`;
    case 'boolean':
      return `${label} is ${condition.value ? 'Yes' : 'No'}`;
    case 'text':
    {
      // Pick-list values read as their labels ("Lapsed", not "lapsed"); a value no option covers reads as stored.
      const words = textValueWords(condition, fieldOptions);
      return condition.operator === 'one-of' ? `${label} is one of ${words.join(', ')}` : `${label} ${condition.operator === 'eq' ? 'is' : 'is not'} ${words.join(', ')}`;
    }
    case 'list':
      return `${label} ${condition.operator === 'contains' ? 'contain' : 'don’t contain'} ${condition.value}`;
    case 'presence':
      return `${label} ${condition.operator === 'is-set' ? 'has a value' : 'is empty'}`;
    case 'date':
      if (condition.operator === 'between') return `${label} is between ${formatDate(condition.start)} and ${formatDate(condition.end)}`;
      if (condition.operator !== 'age') return `${label} is ${condition.operator} ${formatDate(condition.value)}`;
      return `${label} ${agePhrase(condition.comparison, condition.amount, condition.unit)}`;
    default:
      return label;
  }
}

// Stored values renamed since they were saved (the contact-sync rename of 2026-08-11). Sync now
// sends only the new value, so a rule on an old one matches almost no one.
const RENAMED_VALUES: Record<string, ReadonlySet<string>> = {
  signup_source: new Set(['payments', 'portal_page', 'portal_widget']),
};

/**
 * A text rule's values in the dashboard's words. A renamed stored value is quoted as an older
 * value ("“payments” (older value)"), never relabelled as the option it became, because the rule
 * still matches the old value; any other value no option covers reads as stored.
 */
function textValueWords(condition: Extract<AudienceCondition, { type: 'text' }>, fieldOptions: FieldOptions): string[] {
  const renamed = RENAMED_VALUES[condition.field];
  const older = renamed ? condition.values.filter((value) => renamed.has(value)) : [];
  const current = condition.values.filter((value) => !older.includes(value));
  return [...valueWords(fieldOptions[condition.field], current), ...[...new Set(older)].map((value) => `“${value}” (older value)`)];
}

// Yes/no fields read as "who …" clauses; everything else as "whose <field> …".
// [yes, no] after "People" (plural), and the same after "anyone" (singular).
const BOOLEAN_CLAUSES: Record<string, { plural: [string, string]; singular: [string, string] }> = {
  is_lybunt: {
    plural: ['paid last year but not this year', 'didn’t pay last year, or paid this year'],
    singular: ['paid last year but not this year', 'didn’t pay last year, or paid this year'],
  },
  has_active_subscription: {
    plural: ['have an active subscription', 'have no active subscription'],
    singular: ['has an active subscription', 'has no active subscription'],
  },
};

function clause(condition: AudienceCondition, currency: string, fieldOptions: FieldOptions, singular = false): string {
  if (condition.type === 'boolean' && BOOLEAN_CLAUSES[condition.field]) return `who ${BOOLEAN_CLAUSES[condition.field]![singular ? 'singular' : 'plural'][condition.value ? 0 : 1]}`;
  if (condition.type === 'giving') return `who ${lowerFirst(describeGiving(condition, fieldOptions))}`;
  if (condition.type === 'list' && condition.field === 'tags') return `${condition.operator === 'contains' ? 'tagged' : 'not tagged'} ${condition.value}`;
  return `whose ${lowerFirst(describeCondition(condition, currency, fieldLabel, fieldOptions))}`;
}

function includeClause(rule: IncludeRule, currency: string, fieldOptions: FieldOptions): string {
  if ('children' in rule) return `(${rule.children.map((child) => clause(child, currency, fieldOptions)).join(rule.type === 'all' ? ' and ' : ' or ')})`;
  return clause(rule, currency, fieldOptions);
}

/** The saved detail's "Who's in it" sentence, or null for a non-canonical definition. */
export function describeSegment(definition: NativeAudienceDefinition, currency: string, fieldOptions: FieldOptions = {}): string | null {
  const sections = toSections(definition);
  if (!sections) return null;
  const parts: string[] = [];
  if (sections.include.length) {
    parts.push(`People ${sections.include.map((rule) => includeClause(rule, currency, fieldOptions)).join(sections.match === 'all' ? ' and ' : ' or ')}`);
  }
  const picked = sections.people.length;
  if (picked) parts.push(`${parts.length ? 'plus ' : ''}${picked.toLocaleString('en-US')} hand-picked ${picked === 1 ? 'person' : 'people'}`);
  let sentence = parts.join(' — ');
  if (sections.exclusions.length) {
    sentence += ` — excluding anyone ${sections.exclusions.map((rule) => clause(rule, currency, fieldOptions, true)).join(' or ')}`;
  }
  return `${sentence}.`;
}

function lowerFirst(text: string): string {
  return text && !/^[A-Z]{2}/.test(text) ? text[0]!.toLowerCase() + text.slice(1) : text;
}

/** True when every value in the sections is filled in, so a preview request is worth sending. */
export function sectionsComplete(sections: SegmentSections): boolean {
  const complete = (condition: AudienceCondition): boolean => {
    switch (condition.type) {
      case 'number': return Number.isFinite(condition.value);
      case 'text': return condition.values.length > 0 && condition.values.every((value) => value.trim().length > 0);
      case 'list': return condition.value.trim().length > 0;
      case 'giving': {
        const values: string[] = condition.scope.kind === 'fund' ? condition.scope.funds : condition.scope.formIds;
        return Number.isFinite(condition.value) && values.length > 0 && values.every((value) => value.trim().length > 0);
      }
      case 'date':
        if (condition.operator === 'age') return Number.isFinite(condition.amount) && condition.amount >= 0;
        if (condition.operator === 'between') return !Number.isNaN(Date.parse(condition.start)) && !Number.isNaN(Date.parse(condition.end)) && Date.parse(condition.start) < Date.parse(condition.end);
        return !Number.isNaN(Date.parse(condition.value));
      default: return true;
    }
  };
  const includeComplete = sections.include.every((rule) => ('children' in rule ? rule.children.length > 0 && rule.children.every(complete) : complete(rule)));
  return includeComplete && sections.exclusions.every(complete) && (sections.include.length > 0 || sections.people.length > 0);
}

/** "1-99" style bucket for analytics, so no exact count leaves the browser. */
export function matchBucket(count: number): '0' | '1-99' | '100-999' | '1k+' {
  if (count <= 0) return '0';
  if (count < 100) return '1-99';
  if (count < 1000) return '100-999';
  return '1k+';
}
